import type { GetRequestsResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import { requestListKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import type { RequestCounts } from "../../types/tables";

interface UseRequestCountsOptions {
  enabled?: boolean;
}

// No parameters needed - tab counts should always show total counts
// regardless of current filter selection

export function useRequestCounts(options: UseRequestCountsOptions = {}) {
  const { data: userData } = useUser();
  const projectId = userData?.currentProjectId;
  const { enabled = true } = options;

  return useQuery({
    enabled: !!userData?.user && enabled,
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    queryFn: async () => {
      const baseParams: Record<string, number | string | string[]> = {
        // Request only minimal fields needed for counts
        fields: ["id"],
        limit: 1, // We only need the total count, not the actual data
        page: 1,
      };

      // Add projectId for customer account filtering
      if (projectId) {
        baseParams.projectId = projectId;
      }

      // No category filter - always show total counts for all request types

      // Fetch counts for all tabs in parallel
      const [
        activeResponse,
        allResponse,
        completedResponse,
        draftsResponse,
        queuedResponse,
      ] = await Promise.all([
        // Active requests
        apiClient
          .url("/requests/list")
          .query({
            ...baseParams,
            tab: "active",
          })
          .get()
          .json<GetRequestsResponse>(),

        // All requests
        apiClient
          .url("/requests/list")
          .query({
            ...baseParams,
            tab: "all",
          })
          .get()
          .json<GetRequestsResponse>(),

        // Completed requests
        apiClient
          .url("/requests/list")
          .query({
            ...baseParams,
            tab: "completed",
          })
          .get()
          .json<GetRequestsResponse>(),

        // Draft requests
        apiClient
          .url("/requests/list")
          .query({
            ...baseParams,
            tab: "drafts",
          })
          .get()
          .json<GetRequestsResponse>(),

        // Queued requests
        apiClient
          .url("/requests/list")
          .query({
            ...baseParams,
            tab: "queued",
          })
          .get()
          .json<GetRequestsResponse>(),
      ]);

      const counts: RequestCounts = {
        active: activeResponse.total || 0,
        all: allResponse.total || 0,
        completed: completedResponse.total || 0,
        drafts: draftsResponse.total || 0,
        queued: queuedResponse.total || 0,
      };

      return counts;
    },
    queryKey: [...requestListKeys.all, "counts", projectId],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 2 * 60 * 1000 : false, // 2 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes - counts change less frequently
  });
}
